/**
 * ProgressRing - animated SVG ring showing percentage progress.
 * Props:
 *   - value: 0..100
 *   - size: px (default 80)
 *   - stroke: px (default 7)
 *   - color: stroke color (default brand blue)
 *   - bgColor: track color (default slate-100)
 *   - label: optional override (default `${value}%`)
 */
export const ProgressRing = ({
  value = 0,
  size = 80,
  stroke = 7,
  color = "#2563EB",
  bgColor = "#E2E8F0",
  label,
  sublabel,
  testId = "progress-ring",
}) => {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - safeValue / 100);

  // Scale the label so it always fits inside the ring (even "100%" in a small 44px ring).
  const text = label !== undefined ? String(label) : `${Math.round(safeValue)}%`;
  const innerD = size - stroke * 2;
  const fontSize = Math.max(9, Math.min(size * 0.26, (innerD * 0.9) / (text.length * 0.6)));

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      data-testid={testId}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={bgColor}
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(.5,.1,.25,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center leading-none px-0.5">
        <span
          className="font-bold text-slate-800 leading-none tabular-nums tracking-tight"
          style={{ fontSize: `${fontSize}px` }}
        >
          {text}
        </span>
        {sublabel && (
          <span
            className="uppercase tracking-wider text-slate-400 leading-none mt-0.5"
            style={{ fontSize: `${Math.max(7, fontSize * 0.5)}px` }}
          >
            {sublabel}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProgressRing;
